/**
 * ═══════════════════════════════════════════════════════════════════════════
 * Comandero — Food-cost (LÓGICA PURA)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * CC-6: este archivo es `.ts` PURO. NO importa macros de lingui (`msg`/`t`) ni
 * nada de UI. Recibe números (centavos / cantidades enteras) y devuelve números.
 *
 * Convenciones:
 *   - `ingredient.cost` es el costo en CENTAVOS por unidad de stock (g/ml/unit).
 *   - `recipe_item.qty` es la cantidad de ese ingrediente por unidad vendida.
 *   - El costo de una receta = Σ(qty × cost), en centavos.
 *   - food-cost % = costo receta / precio de venta (0..1; ×100 para mostrar).
 */

export interface RecipeCostLine {
  /** Cantidad del ingrediente por unidad vendida (entero ≥ 0). */
  qty: number;
  /** Costo del ingrediente en centavos por unidad de stock. */
  cost: number;
}

/** Costo de una receta = Σ(qty × cost), en centavos. */
export function recipeCost(lines: RecipeCostLine[]): number {
  return lines.reduce(
    (acc, l) => acc + Math.max(0, Math.trunc(l.qty)) * Math.max(0, Math.trunc(l.cost)),
    0,
  );
}

/**
 * Food-cost como fracción del precio de venta (0..1). Devuelve 0 si el precio es
 * ≤ 0 (evita dividir por cero / porcentajes infinitos en productos sin precio).
 */
export function foodCostRatio(cost: number, salePrice: number): number {
  const price = Math.trunc(salePrice);
  if (price <= 0) {
    return 0;
  }
  return Math.max(0, cost) / price;
}

/** Food-cost en PUNTOS PORCENTUALES redondeados (p. ej. 33 = 33 %). */
export function foodCostPercent(cost: number, salePrice: number): number {
  return Math.round(foodCostRatio(cost, salePrice) * 100);
}

/** Margen bruto unitario = precio − costo, en centavos (puede ser negativo). */
export function grossMargin(cost: number, salePrice: number): number {
  return Math.trunc(salePrice) - Math.max(0, cost);
}
